/**
 * 認證模組共用型別定義
 * 從 authService.ts 提取，避免循環依賴
 */

import type { User as SupabaseUser, AuthChangeEvent } from '@supabase/supabase-js'

// ============ 類型定義 ============

/** Auth 事件類型 */
export type AuthEvent = AuthChangeEvent | 'INITIAL_SESSION'

/** 認證用戶（統一格式） */
export interface AuthUser {
  id: string                    // Supabase user ID
  uid: string                   // 相容欄位 (= id)
  email: string | null
  displayName: string | null
  photoURL: string | null
  emailVerified: boolean
  isSupabaseUser: boolean
  supabaseUser?: SupabaseUser
  lastSelectedOrgId?: string | null
  isRecovered?: boolean         // 是否為恢復的用戶
}

/** Firebase 相容格式的用戶物件 (向後相容別名) */
/** @deprecated Use AuthUser instead */
export type FirebaseCompatibleUser = AuthUser

/** 用戶 Profile (資料庫完整資料，camelCase 格式) */
export interface UserProfile {
  id?: string
  uid?: string
  email?: string | null
  displayName?: string | null
  fullName?: string | null
  photoURL?: string | null
  phoneNumber?: string | null
  title?: string | null
  department?: string | null
  address?: string | null
  googleId?: string | null
  facebookId?: string | null
  lineId?: string | null
  createdAt?: string | null
  updatedAt?: string | null
  lastSelectedOrgId?: string | null
  lastOrgUpdateAt?: string | null
  supabaseUserId?: string | null
}

/** 用戶資料（資料庫格式，snake_case） */
export interface UserRow {
  id: string
  email: string | null
  display_name: string | null
  full_name: string | null
  photo_url: string | null
  phone_number: string | null
  title: string | null
  department: string | null
  address: string | null
  google_id: string | null
  facebook_id: string | null
  line_id: string | null
  created_at: string | null
  updated_at: string | null
  last_selected_org_id: string | null
  last_org_update_at: string | null
  supabase_user_id: string | null
  last_selected_filter?: string | null
  last_selected_city?: string | null
  last_filter_update_at?: string | null
}

/** Auth 狀態變化回調 */
export type AuthStateCallback = (event: AuthEvent, user: AuthUser | null) => void

/** 快取統計資訊 */
export interface AuthStats {
  isCached: boolean
  hasSession: boolean
  cacheExpireIn: number
  isAuthReady: boolean
  hasUser: boolean
}

/** OAuth 錯誤 */
export interface OAuthError extends Error {
  code?: string
}

/** OAuth Provider 名稱 */
export type ProviderName = 'google' | 'facebook' | 'line' | 'oidc.line' | 'google.com' | 'facebook.com' | string

// ============ 輔助函數 ============

/**
 * 將 Supabase user 轉換為 AuthUser 格式
 */
export function convertSupabaseUserToAuthUser(supabaseUser: SupabaseUser | null): AuthUser | null {
  if (!supabaseUser) return null

  return {
    id: supabaseUser.id,
    uid: supabaseUser.id,
    email: supabaseUser.email || null,
    displayName:
      supabaseUser.user_metadata?.display_name ||
      supabaseUser.user_metadata?.full_name ||
      supabaseUser.user_metadata?.name ||
      null,
    photoURL:
      supabaseUser.user_metadata?.photo_url ||
      supabaseUser.user_metadata?.avatar_url ||
      supabaseUser.user_metadata?.picture ||
      null,
    emailVerified: !!supabaseUser.email_confirmed_at,
    isSupabaseUser: true,
    supabaseUser: supabaseUser
  }
}
